import {
  errMissingDataInTermValues,
  errUnexpectedCalculationName,
} from "@/app/errors/errors.metric_utils"
import { mainLogger as log } from "@/app/logger"
import {
  AllValues,
  Calculation,
  Chart,
  ComparisonNew,
  Entity,
  EntityValues,
  GroupMemberValues,
  GroupValues,
  List,
  Term,
  TermValues,
  Top10Values,
  Values,
} from "@/app/types"
import { isArray, isNotNull, isNull, isObjectWithProps } from "@/app/utils"

export function getPeers(
  isFund: boolean,
  entityMetrics: EntityValues | null
): GroupValues | undefined {
  const sets = isFund ? entityMetrics?.fundsets : entityMetrics?.firmsets
  return sets?.peers
}

// TODO: make calc into its own class instead of string
export function getValuesForCalc(
  groupValues: GroupValues,
  calc: string
): TermValues {
  if (!Calculation.CALCS.includes(calc))
    errUnexpectedCalculationName(Calculation.CALCS, calc)
  if (!groupValues?.[calc])
    return {
      periodic: [],
      ranking: {},
    }
  return groupValues[calc]
}

export function getPeersMembers(
  isFund: boolean,
  entityMetrics: EntityValues | null
): List<GroupMemberValues> {
  const peers = getPeers(isFund, entityMetrics)
  return peers ? peers.members : []
}

export function getTop10(
  allMetrics: AllValues | null,
  top10MetricName: string
): Top10Values | undefined {
  return (
      isNotNull(allMetrics) &&
        isObjectWithProps(allMetrics?.top10, top10MetricName)
    ) ?
      allMetrics?.top10[top10MetricName]
    : undefined
}

export function getTop10Members(
  allMetrics: AllValues | null,
  top10MetricName: string,
  isPeriodic: boolean,
  termKey: string
): List<GroupMemberValues> {
  const top10 = getTop10(allMetrics, top10MetricName)
  // check if top10 is missing
  if (!top10) return []
  // return top10 for periodic charts
  if (isPeriodic) return top10.members.periodic
  // for ranking charts, check if we have the top10 for this term
  if (isObjectWithProps(top10.members.ranking, termKey))
    return top10.members.ranking[termKey]
  // return empty list by default
  return []
}

export function getAllMembers(
  allMetrics: AllValues | null,
  isFund: boolean
): List<GroupMemberValues> {
  if (!allMetrics) return []

  return (
    allMetrics?.[isFund ? "fundsets" : "firmsets"]?.[
      isFund ? "category" : "industry"
    ]?.members ?? []
  )
}

// this method returns a shallow copy of underlying list of members
// the returned list is safe to sort or otherwise mutate, but the objects are not
// TODO - use Object.freeze() after loading the API to ensure no changes can be done on the underlying data
export function getMembers(
  entity: Entity,
  comparison: ComparisonNew,
  entityMetrics: EntityValues | null,
  allMetrics: AllValues | null,
  chart: Chart,
  term: Term
): List<GroupMemberValues> {
  switch (true) {
    case comparison.isPeers():
      return Array.from(getPeersMembers(entity.isFund(), entityMetrics))
    case comparison.isAll():
      const members = Array.from(getAllMembers(allMetrics, entity.isFund()))
      //TODO - remove sorting when the API returns sorted by default
      const entityProp = entity.propertyName()
      return members.toSorted((a, b) => {
        const aName = a[entityProp].name?.toUpperCase()
        const bName = b[entityProp].name?.toUpperCase()
        return (aName ?? "").localeCompare(bName ?? "")
      })
    case comparison.isTop10():
      const top10By = chart.top10MetricName
      const isPeriodic = chart.isPeriodic()
      return Array.from(
        getTop10Members(allMetrics, top10By, isPeriodic, term.key)
      )
    case comparison.isCustomList():
      const customListMembers =
        entityMetrics?.[entity.isFund() ? "fundsets" : "firmsets"]
          ?.customMetrics?.members
      if (customListMembers) return customListMembers
  }

  log.error(
    "[getMembers] Error getting members because of unknown comparison",
    {
      Comparison: comparison,
      Entity: entity,
    }
  )
  return []
}

export function getValuesForPeriod(
  termValues: TermValues,
  periodicIdx: number | null = null
): Values | null {
  const periodicValues = termValues.periodic

  if (!isArray(periodicValues))
    errMissingDataInTermValues("periodic", { "Term Values": termValues })
  if (!periodicIdx) return periodicValues[0] //latest period by default
  const values = periodicValues.find((v: Values) => v.idx == periodicIdx)
  if (!values) {
    log.error("[getValuesForPeriod] Error finding periodic values for index", {
      Index: periodicIdx,
      "Term Values": termValues,
    })
    return null
  }

  return values
}

export function getValuesForTerm(
  termValues: TermValues,
  termKey: string
): Values | null {
  const rankingValues = termValues.ranking

  if (isNull(rankingValues))
    errMissingDataInTermValues("ranking", { "Term Values": termValues })

  if (!(termKey in rankingValues)) {
    log.error("[getValuesForTerm] Error finding values for term", {
      "Term Key": termKey,
      "Term Values": termValues,
    })
    return null
  }

  return rankingValues[termKey]
}

export function getValueForMetric(
  termValues: TermValues,
  isPeriodic: boolean,
  periodicIdx: number | null,
  termKey: string,
  metricName: string
): number | null {
  const values =
    isPeriodic ?
      getValuesForPeriod(termValues, periodicIdx)
    : getValuesForTerm(termValues, termKey)

  return getValue(values, metricName)
}

export function getPeriodicValueForMetric(
  termValues: TermValues,
  periodicIdx: number | null,
  metricName: string
): number | null {
  const values = getValuesForPeriod(termValues, periodicIdx)
  return getValue(values, metricName)
}

export function getLatestPeriodicValueForMetric(
  termValues: TermValues,
  metricName: string
): number | null {
  return getPeriodicValueForMetric(termValues, null, metricName)
}

export function getRankingValueForMetric(
  termValues: TermValues,
  termKey: string,
  metricName: string
): number | null {
  const values = getValuesForTerm(termValues, termKey)
  return getValue(values, metricName)
}

export function getValueForMetrics(
  termValues: TermValues,
  isPeriodic: boolean,
  periodicIdx: number,
  termKey: string,
  metricNames: string[]
): (number | null)[] {
  return metricNames.map((metricName) =>
    getValueForMetric(termValues, isPeriodic, periodicIdx, termKey, metricName)
  )
}

export function getValue(
  values: Values | null,
  metricName: string
): number | null {
  if (!values) {
    log.error("[getValue] Error finding value", {
      Values: values,
      "Metric Name": metricName,
    })
    return null
  }

  if (!(metricName in values)) {
    log.warn("Missing metric name", metricName, " in values: ", {
      Values: values,
    })

    return null
  }

  return values[metricName] === null ? null : Number(values[metricName])
}

export function getCalculationValuesForComparison(
  entityValues: EntityValues | null,
  allValues: AllValues | null,
  comparison: ComparisonNew,
  term: Term,
  isFund: boolean,
  metricName: string
) {
  const isCustomList = comparison.isCustomList()
  const calculation = comparison.getCalculation()

  if (isCustomList || comparison.isPeers()) {
    if (!entityValues) return null

    const groupValues =
      entityValues[isFund ? "fundsets" : "firmsets"]?.[
        isCustomList ? "customMetrics" : "peers"
      ]

    const termValues = getValuesForCalc(groupValues!, calculation)

    return getValueForMetric(
      termValues,
      term.isPeriodic,
      null,
      term.key,
      metricName
    )
  }

  if (comparison.isAll()) {
    if (!allValues) return null

    const groupValues =
      allValues[isFund ? "fundsets" : "firmsets"]?.[
        isFund ? "category" : "industry"
      ]

    const termValues = getValuesForCalc(
      groupValues!,
      comparison.getCalculation()
    )

    return getValueForMetric(
      termValues,
      term.isPeriodic,
      null,
      term.key,
      metricName
    )
  }

  if (comparison.isTop10()) {
    if (!allValues) return null

    const groupValues = allValues?.top10?.[metricName] as unknown as GroupValues

    const termValues = getValuesForCalc(
      groupValues,
      comparison.getCalculation()
    )

    return getValueForMetric(
      termValues,
      term.isPeriodic,
      null,
      term.key,
      metricName
    )
  }
}
